/**
 * HierarchySwitcherSheet — Spec 287: mobile bottom-sheet Hierarchy switcher,
 * opened from HierarchyChip. Built on the existing Sheet primitive (grabber,
 * 16px top radius, --theme-bg-modal surface, 44px close, safe-area footer —
 * all owned by components/ui/Sheet.tsx, not reimplemented here).
 *
 * Lists every Hierarchy the user belongs to (name, role via isDomRole(),
 * member count, Active badge on the current selection) plus a labeled
 * "Manage Hierarchies" footer link. Tapping a non-active row switches via
 * selectOrganization() and closes the sheet; tapping the active row just
 * closes it.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useOrganization, Organization } from '../../contexts/OrganizationContext';
import { useTerminology } from '../../hooks/useTerminology';
import { isDomRole } from '../../utils/roleHelpers';

interface HierarchySwitcherSheetProps {
  open: boolean;
  onDismiss: () => void;
}

// "Just you" mirrors mockup 02's solo-Hierarchy meta line; omitted entirely
// when the count is unavailable (row degrades to role label only).
function memberCountLabel(count?: number): string | null {
  if (count == null) return null;
  return count === 1 ? 'Just you' : `${count} members`;
}

function ChevronRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

const HierarchySwitcherSheet: React.FC<HierarchySwitcherSheetProps> = ({ open, onDismiss }) => {
  const { organization, organizations, selectOrganization } = useOrganization();
  const { t } = useTerminology();

  const handleSelect = (org: Organization) => {
    if (org.id !== organization?.id) {
      selectOrganization(org);
    }
    onDismiss();
  };

  return (
    <Sheet
      open={open}
      onDismiss={onDismiss}
      title="Switch Hierarchy"
      footer={
        <Link
          to="/organizations"
          onClick={onDismiss}
          data-testid="hierarchy-switcher-manage"
          className="inline-flex items-center gap-1.5 min-h-touch px-2 text-[var(--theme-primary)] text-sm font-semibold no-underline"
        >
          Manage Hierarchies
          <ChevronRightIcon />
        </Link>
      }
    >
      <div className="flex flex-col divide-y divide-border-light" data-testid="hierarchy-switcher-list">
        {organizations.map((org) => {
          const isActive = org.id === organization?.id;
          const roleLabel = isDomRole(org.role) ? t('dominant') : t('submissive');
          const memberLabel = memberCountLabel(org.memberCount);

          return (
            <Button
              key={org.id}
              type="button"
              variant="ghost"
              fullWidth
              aria-current={isActive ? 'true' : undefined}
              data-testid={`hierarchy-switcher-row-${org.id}`}
              onClick={() => handleSelect(org)}
              className={`h-auto min-h-[64px] py-2.5 justify-between text-left rounded-lg gap-3 ${
                isActive
                  ? 'bg-[var(--theme-bg-tertiary)] hover:bg-[var(--theme-bg-tertiary)]'
                  : 'bg-transparent hover:bg-[var(--theme-bg-tertiary)]'
              }`}
            >
              <span className="flex items-center gap-3 min-w-0">
                <span
                  aria-hidden="true"
                  className={`shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center text-sm font-bold border ${
                    isActive
                      ? 'bg-[var(--theme-primary-light)] border-[var(--theme-primary)] text-[var(--theme-primary)]'
                      : 'bg-[var(--theme-bg-tertiary)] border-border-light text-text-secondary'
                  }`}
                >
                  {org.name.charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex flex-col items-start">
                  <span className="text-[15px] font-semibold text-text-primary truncate max-w-[180px]">
                    {org.name}
                  </span>
                  <span className="text-xs text-text-secondary">
                    <span
                      className={
                        isDomRole(org.role)
                          ? 'text-[var(--theme-role-dom)] font-semibold'
                          : 'text-[var(--theme-role-sub)] font-semibold'
                      }
                    >
                      {roleLabel}
                    </span>
                    {memberLabel ? ` · ${memberLabel}` : ''}
                  </span>
                </span>
              </span>
              {isActive && (
                <Badge variant="success" size="sm" className="shrink-0">
                  Active
                </Badge>
              )}
            </Button>
          );
        })}
      </div>
    </Sheet>
  );
};

export default HierarchySwitcherSheet;
